import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Ticket,
  Check,
  Star,
  Laptop,
  Globe,
} from "lucide-react";

const API_BASE = "http://localhost:5001";

// Static events (kept as plain JS objects)
const STATIC_EVENTS = [
  {
    id: 1,
    title: "Agency Growth Accelerator",
    subtitle: "The Ultimate Digital Bootcamp",
    date: "2025-01-10",
    image_url:
      "https://images.pexels.com/photos/1181352/pexels-photo-1181352.jpeg?auto=compress&cs=tinysrgb&w=1200",
    location: "Virtual Campus",
    price: 9999,
    description:
      "A 5-day immersive online sprint for insurance agency owners. Automate recruitment, master digital branding, and scale revenue without leaving your office.",
    features: [
      "Digital Recruitment Funnels",
      "Remote Team Management",
      "Social Media Branding",
      "Live Q&A with Experts",
    ],
  },
  {
    id: 2,
    title: "The Limitless Mind: Online Immersion",
    subtitle: "3-Day Live Virtual Experience",
    date: "2025-02-15",
    image_url:
      "https://images.pexels.com/photos/1181533/pexels-photo-1181533.jpeg?auto=compress&cs=tinysrgb&w=1200",
    location: "Virtual Campus (Live Stream)",
    price: 14999,
    description:
      "Rewire your subconscious mind from the comfort of your home. A 3-day deep dive into fear removal, identity shifting, and high-performance psychology via HD live stream.",
    features: [
      "Subconscious Reprogramming",
      "Live Breakout Sessions",
      "Daily Mindset Rituals",
      "High-Performance Coaching",
    ],
  },
  {
    id: 3,
    title: "Scientific Manifestation 2.0",
    subtitle: "30-Day Online Cohort",
    date: "2025-03-01",
    image_url:
      "https://images.pexels.com/photos/927414/pexels-photo-927414.jpeg?auto=compress&cs=tinysrgb&w=1200",
    location: "Global Digital Classroom",
    price: 4999,
    description:
      "The science of getting what you want. We combine neuroscience with ancient wisdom to help you visualize and attract your goals using our proprietary Neuro-Sync frameworks.",
    features: [
      "Daily Audio Priming",
      "Weekly Group Coaching",
      "Visualization Protocols",
      "Digital Workbook",
    ],
  },
  {
    id: 4,
    title: "Legacy Leadership: Digital Mastermind",
    subtitle: "Elite Online Strategy Boardroom",
    date: "2025-04-20",
    image_url:
      "https://images.pexels.com/photos/1181356/pexels-photo-1181356.jpeg?auto=compress&cs=tinysrgb&w=1200",
    location: "Global Digital Classroom",
    price: 29999,
    description:
      "For the top 1% of agency leaders. A strictly limited virtual mastermind focused on succession planning, wealth creation, and building a self-sustaining business legacy.",
    features: [
      "Business Systemization",
      "Virtual Networking",
      "Wealth Management",
      "Exclusive Strategy Docs",
    ],
  },
];

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/events`);
        const data = await res.json();

        if (Array.isArray(data) && data.length > 0) {
          setEvents(data);
        } else {
          setEvents(STATIC_EVENTS);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents(STATIC_EVENTS);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  return (
    <div className="bg-exiser-light min-h-screen">
      {/* Hero */}
      <div className="bg-exiser-primary text-white py-24 text-center relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-exiser-primary z-10" />
        <img
          src="https://images.unsplash.com/photo-1544531586-fde5298cdd40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
          alt="Audience"
          className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:scale-105 transition-transform duration-[20s]"
        />

        <div className="relative z-20 max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-1 rounded-full mb-6 backdrop-blur-sm">
            <Star className="w-4 h-4 text-exiser-accent fill-current" />
            <span className="text-xs font-bold uppercase tracking-widest">
              World Class Events
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold uppercase mb-6 drop-shadow-lg animate-fade-in-up leading-none">
            Upcoming <span className="text-exiser-accent">Events</span>
          </h1>
          <p className="text-xl text-purple-200 font-sans max-w-2xl mx-auto animate-fade-in-up delay-100">
            Total immersion. Total transformation. Join our world-class events
            from anywhere.
          </p>
        </div>
      </div>

      {/* Events List */}
      <div className="max-w-6xl mx-auto px-4 py-20 -mt-16 relative z-30">
        {loading ? (
          <div className="text-center text-gray-500 font-medium">
            Loading events...
          </div>
        ) : (
          <div className="grid gap-12">
            {events.map((course) => {
              const locationText = course.location || "";
              const isVirtual =
                locationText.toLowerCase().includes("virtual") ||
                locationText.toLowerCase().includes("digital") ||
                locationText.toLowerCase().includes("online");

              return (
                <div
                  key={course.id}
                  className="flex flex-col md:flex-row bg-white rounded-lg shadow-xl overflow-hidden hover:shadow-2xl hover:shadow-purple-900/20 transition-all duration-500 border border-purple-100 group transform hover:-translate-y-1"
                >
                  {/* Image Section */}
                  <div className="md:w-5/12 h-64 md:h-auto overflow-hidden relative">
                    <div
                      className={`absolute top-4 left-4 font-bold px-4 py-2 uppercase text-xs z-10 shadow-lg flex items-center gap-2 ${isVirtual
                        ? "bg-blue-600 text-white"
                        : "bg-exiser-accent text-exiser-primary"
                        }`}
                    >
                      {isVirtual ? (
                        <Laptop className="w-3 h-3" />
                      ) : (
                        <MapPin className="w-3 h-3" />
                      )}{" "}
                      {isVirtual ? "Online Event" : "Physical Event"}
                    </div>
                    <img
                      src={
                        course.image_url ||
                        "https://via.placeholder.com/800x500?text=Event"
                      }
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
                    />
                    <div className="absolute inset-0 bg-exiser-primary/10 group-hover:bg-transparent transition-colors duration-500" />
                  </div>

                  {/* Content Section */}
                  <div className="p-8 md:p-10 md:w-7/12 flex flex-col relative bg-white">
                    <div className="absolute top-10 right-10 opacity-[0.03] pointer-events-none transform translate-x-10 group-hover:translate-x-0 transition-transform duration-700">
                      <Ticket className="w-64 h-64" />
                    </div>

                    <div className="relative z-10">
                      <div className="flex flex-wrap justify-between items-start mb-4 gap-4">
                        <div>

                          <h3 className="text-2xl md:text-3xl font-bold uppercase text-gray-900 group-hover:text-exiser-primary transition-colors leading-tight mb-1">
                            {course.title}
                          </h3>
                          {course.subtitle && (
                            <p className="text-sm font-bold text-exiser-accent uppercase tracking-wider">
                              {course.subtitle}
                            </p>
                          )}
                        </div>

                      </div>

                      <div className="flex items-center gap-2 text-gray-500 font-sans mb-6 border-b border-gray-100 pb-6">
                        {isVirtual ? (
                          <Globe className="w-4 h-4 text-blue-500" />
                        ) : (
                          <MapPin className="w-4 h-4 text-red-500" />
                        )}
                        <span className="font-bold">{course.location}</span>
                      </div>

                      {course.description && (
                        <p className="text-gray-600 font-sans mb-8 leading-relaxed text-sm md:text-base">
                          {course.description}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10 relative z-10">
                      {course.features &&
                        course.features.map((feat, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-3 font-sans text-gray-700 text-sm"
                          >
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span>{feat}</span>
                          </div>
                        ))}
                    </div>

                    <div className="mt-auto relative z-10">
                      <Link
                        to="/book"
                        className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-exiser-primary text-white px-10 py-4 uppercase font-bold text-sm tracking-widest hover:bg-purple-900 transition-all shadow-lg rounded-sm animate-[pulse_3s_ease-in-out_infinite] hover:animate-none hover:scale-105 ring-4 ring-purple-100 group-hover:ring-purple-200"
                      >
                        <Ticket className="w-4 h-4" /> Book Your Event Seat
                      </Link>
                      <p className="text-xs text-gray-400 mt-3 font-sans italic text-center md:text-left">
                        *{" "}
                        {isVirtual
                          ? "Digital seats filling fast."
                          : "Limited seats available."}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;